import { useDeleteCbamSimulation } from '@/features/cbam-simulations-v2/hooks/cbamSimulations'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import { Button, GenericDialog } from '@/common'
import { useCallback } from 'react'
import toast from 'react-hot-toast'
import { Trans } from 'react-i18next'
import { trackCbamSimulationSignal } from '@/features/cbam-simulations-v2/analytics/cbamSimulationClarity'

export default function DeleteSimulationDialog({
  isOpen,
  close,
  simulationName,
  simulationId,
  onAfterLeave
}: {
  isOpen: boolean
  close: () => void
  simulationName: string
  simulationId: string
  onAfterLeave?: () => void
}) {
  const { t } = useI18n()
  const organizationId = useSession().session.organization.id
  const { mutateAsync: deleteSimulation, isPending: isDeleting } = useDeleteCbamSimulation(
    organizationId,
    simulationId
  )

  const onDeleteClick = useCallback(async () => {
    try {
      await deleteSimulation()
      trackCbamSimulationSignal('simulations:delete:succeeded')
      toast.success(
        t('cbamSimulations:cbamSimulationsPage.dialogs.deleteSimulation.notifications.success')
      )
      close()
      onAfterLeave?.()
    } catch (error) {
      console.error(error)
      trackCbamSimulationSignal('simulations:delete:failed')
      toast.error(
        t('cbamSimulations:cbamSimulationsPage.dialogs.deleteSimulation.notifications.error')
      )
    }
  }, [deleteSimulation, close, t, onAfterLeave])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={close}
      title={t('cbamSimulations:cbamSimulationsPage.dialogs.deleteSimulation.title')}
      size="md"
      onAfterLeave={onAfterLeave}
    >
      <div className="space-y-4">
        <p>
          <Trans
            i18nKey="cbamSimulations:cbamSimulationsPage.dialogs.deleteSimulation.description"
            values={{ name: simulationName }}
            components={{ strong: <strong /> }}
          />
        </p>
        <footer className="gap-2 flex justify-end">
          <Button variant="neutral" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="danger" onClick={onDeleteClick} isLoading={isDeleting}>
            {t('common:actions.delete')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
